import {
    definePublicRouteLoaders,
    type PublicRouteLoaderResult,
} from "@logscord/public-site";
import {
    PublicServerDirectoryResponseSchema,
    PublicServerProfileSchema,
    PublicTrustResponseSchema,
    resolvePublicServerCanonicalRoute,
} from "@logscord/public-server";
import {
    DefaultLocale,
    LogscordPublicApiUrls,
    PublicArticleDirectoryResponseSchema,
    PublicArticlePageResponseSchema,
    getPublicLocaleCodeFromLocale,
    getPublicLocaleFromPath,
    isPublicTeamView,
    type LogscordPublicComponentId,
    type LogscordPublicRouteData,
} from "@logscord/public-site-config";


//////////////////////////////////////////////////
//  BROWSER PUBLIC DATA LOADERS
//////////////////////////////////////////////////


async function fetchPublicJson(
    url: string,
    signal: AbortSignal | undefined,
) {
    return fetch(url, {
        signal,
        headers: {
            accept: "application/json",
        },
    });
}

function unavailable(
    response: Response,
): Extract<PublicRouteLoaderResult, { kind: "unavailable" }> {
    const retryAfterHeader = response.headers.get("retry-after");
    const retryAfter = retryAfterHeader && /^\d+$/.test(retryAfterHeader)
        ? Number(retryAfterHeader)
        : undefined;

    return {
        kind: "unavailable",
        ...(retryAfter === undefined ? {} : { retryAfter }),
    };
}

function getPublicApiLocaleCode(pathname: string) {
    return getPublicLocaleCodeFromLocale(
        getPublicLocaleFromPath(pathname) ?? DefaultLocale,
    );
}

export const LogscordBrowserPublicLoaders = definePublicRouteLoaders<
    LogscordPublicComponentId,
    LogscordPublicRouteData
>({
    trust: async ({ signal }) => {
        const response = await fetchPublicJson(LogscordPublicApiUrls.trust, signal);

        if (!response.ok)
            return unavailable(response);

        return {
            kind : "ok",
            data : {
                publicTrust: PublicTrustResponseSchema.parse(await response.json()),
            },
        };
    },
    servers: async ({ searchParams, signal }) => {
        const query = searchParams.toString();
        const response = await fetchPublicJson(
            `${LogscordPublicApiUrls.servers}${query ? `?${query}` : ""}`,
            signal,
        );

        if (!response.ok)
            return unavailable(response);

        return {
            kind : "ok",
            data : {
                publicServers: PublicServerDirectoryResponseSchema.parse(await response.json()),
            },
        };
    },
    team: async ({ signal }) => {
        const response = await fetchPublicJson(LogscordPublicApiUrls.team, signal);

        if (!response.ok)
            return unavailable(response);

        const publicTeam: unknown = await response.json();

        if (!isPublicTeamView(publicTeam))
            throw new Error("The public team response is invalid.");

        return {
            kind : "ok",
            data : {
                publicTeam,
            },
        };
    },
    server: async ({ pathname, params, signal }) => {
        const serverRef = params["serverRef"] ?? "";
        const response = await fetchPublicJson(
            LogscordPublicApiUrls.server(serverRef),
            signal,
        );

        if (response.status === 404)
            return { kind: "not-found" };

        if (!response.ok)
            return unavailable(response);

        const publicServer = PublicServerProfileSchema.parse(await response.json());
        const routeDecision = resolvePublicServerCanonicalRoute(pathname, serverRef, publicServer);

        if (routeDecision.kind === "redirect")
            return {
                kind       : "redirect",
                location   : routeDecision.location,
                statusCode : routeDecision.statusCode,
            };

        return {
            kind : "ok",
            data : {
                publicServer,
            },
            canonicalPath: routeDecision.canonicalPath,
        };
    },
    articles: async ({ pathname, signal }) => {
        const localeCode = getPublicApiLocaleCode(pathname);
        const response = await fetchPublicJson(LogscordPublicApiUrls.articles(localeCode), signal);

        if (!response.ok)
            return unavailable(response);

        const publicArticles = PublicArticleDirectoryResponseSchema.parse(
            await response.json(),
        );

        return {
            kind : "ok",
            data : { publicArticles },
        };
    },
    article: async ({ pathname, params, signal }) => {
        const localeCode = getPublicApiLocaleCode(pathname);
        const response = await fetchPublicJson(
            LogscordPublicApiUrls.article(params["slug"] ?? "", localeCode),
            signal,
        );

        if (response.status === 404)
            return { kind: "not-found" };

        if (!response.ok)
            return unavailable(response);

        const publicArticlePage = PublicArticlePageResponseSchema.parse(
            await response.json(),
        );

        return {
            kind : "ok",
            data : { publicArticlePage },
        };
    },
});
